import * as React from "react";
import useKBConnection from "../hooks/useKBConnection";
import UploadButton from "./UploadButton";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import useTeams from "@/hooks/useTeams";
import ColorPicker from "@/components/ColorPicker";
import {
  PrimaryBlueButton
} from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

export default function Index() {
  const { connected } = useKBConnection();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [companyName, setCompanyName] = React.useState("");
  const [themeColor, setThemeColor] = React.useState("");
  const [headerText, setHeaderText] = React.useState("");
  const [returnLinkText, setReturnLinkText] = React.useState("");
  const [returnLinkUrl, setReturnLinkUrl] = React.useState("");
  const [logoUrl, setLogoUrl] = React.useState("");
  const [enableLiveChat, setEnableLiveChat] = React.useState(false);
  const [liveChatGroupId, setLiveChatGroupId] = React.useState<number>(0);
  const [domain, setDomain] = React.useState("");
  const [effectiveDomain, setEffectiveDomain] = React.useState("");
  const [domainStatus, setDomainStatus] = React.useState<string | null>(null);
  const [frontDoorEndpoint, setFrontDoorEndpoint] = React.useState("");
  const [showHelp, setShowHelp] = React.useState(false);
  const [urlCopied, setUrlCopied] = React.useState(false);
  const [searchEnabled, setSearchEnabled] = React.useState<boolean | null>(null);
  const [reindexing, setReindexing] = React.useState(false);
  const [reindexResult, setReindexResult] = React.useState<{ count: number; message: string } | null>(null);
  const [syncing, setSyncing] = React.useState(false);
  const [syncMessage, setSyncMessage] = React.useState<string | null>(null);
  const [enableKbBotIndexing, setEnableKbBotIndexing] = React.useState(true);
  const [kbAutoReindexIntervalHours, setKbAutoReindexIntervalHours] = React.useState(24);
  const [hasAI, setHasAI] = React.useState(false);
  const [showLastUpdated, setShowLastUpdated] = React.useState(true);
  const [enableKbBot, setEnableKbBot] = React.useState(false);
  const [kbChatWindowId, setKbChatWindowId] = React.useState(0);
  // Health stats
  const [lastReindexAt, setLastReindexAt] = React.useState<string | null>(null);
  const [lastReindexCount, setLastReindexCount] = React.useState(0);
  const [staleCount, setStaleCount] = React.useState(0);
  const [totalPublished, setTotalPublished] = React.useState(0);
  // Monthly usage stats
  const [usageMonth, setUsageMonth] = React.useState("");
  const [embeddingCalls, setEmbeddingCalls] = React.useState(0);
  const [reindexOps, setReindexOps] = React.useState(0);
  const [usageLoaded, setUsageLoaded] = React.useState(false);
  const [syncError, setSyncError] = React.useState<string | null>(null);
  // Search preview
  const [previewQuery, setPreviewQuery] = React.useState("");
  const [previewResults, setPreviewResults] = React.useState<Array<{
    title: string; description: string; url: string; topicName: string;
    enableBotResponse: boolean; score: number;
  }>>([]);
  const [previewing, setPreviewing] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const { teams } = useTeams();
  const api = useApi();
  const toastAlert = useToastAlert();

  React.useEffect(() => {
    async function fetchTenant() {
      setLoading(true);
      try {
        const response = await api.admin.get("api/kb/settings");
        const json = await response.json();
        setCompanyName(json?.companyName ?? "");
        setThemeColor(json?.themeColor ?? "");
        setHeaderText(json?.headerText ?? "");
        setEnableLiveChat(json?.enableLiveChat ?? false);
        setLiveChatGroupId(json?.liveChatGroupId ?? 0);
        setReturnLinkText(json?.returnLinkText ?? "");
        setReturnLinkUrl(json?.returnLinkUrl ?? "");
        setLogoUrl(json?.logoUrl ?? "");
        setDomain(json?.domain ?? "");
        setEffectiveDomain(json?.effectiveDomain ?? "");
        setDomainStatus(json?.domainStatus ?? null);
        setFrontDoorEndpoint(json?.frontDoorEndpoint ?? "");
        setEnableKbBotIndexing(json?.enableKbBotIndexing ?? true);
        setKbAutoReindexIntervalHours(json?.kbAutoReindexIntervalHours ?? 24);
        setHasAI(json?.hasAI ?? false);
        setShowLastUpdated(json?.showLastUpdated ?? true);
        setEnableKbBot(json?.enableKbBot ?? false);
        setKbChatWindowId(json?.kbChatWindowId ?? 0);
      } catch (err) {
        console.error("Failed to fetch KB settings:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchTenant();

    // Load monthly usage stats
    api.admin.get("api/kb/settings/usage").then(async (r) => {
      if (r.ok) {
        const d = await r.json();
        setUsageMonth(d?.month ?? "");
        setEmbeddingCalls(d?.embeddingCalls ?? 0);
        setReindexOps(d?.reindexOps ?? 0);
        setSyncError(d?.syncSucceeded === false ? (d?.syncError ?? "Last sync failed.") : null);
        setUsageLoaded(true);
      }
    }).catch(() => {});

    // Check Azure Search status + reindex health
    api.admin.get("api/kb/search/status").then(async (r) => {
      if (r.ok) {
        const data = await r.json();
        setSearchEnabled(data?.enabled ?? false);
        setLastReindexAt(data?.lastReindexAt ?? null);
        setLastReindexCount(data?.lastReindexCount ?? 0);
        setStaleCount(data?.staleCount ?? 0);
        setTotalPublished(data?.totalPublished ?? 0);
      }
    }).catch(() => setSearchEnabled(false));
  }, []);

  async function handleSubmit() {
    setSaving(true);
    try {
      const resp = await api.admin.post("api/kb/settings", {
        companyName,
        headerText,
        themeColor,
        enableLiveChat,
        liveChatGroupId,
        returnLinkText,
        returnLinkUrl,
        logoUrl,
        enableKbBotIndexing,
        kbAutoReindexIntervalHours,
        showLastUpdated,
        enableKbBot,
        kbChatWindowId,
        domain
      });

      if (resp.status === 400) {
        toastAlert.displayToast(Severity.Error, "Failed to save settings");
      } else {
        toastAlert.displayToast(Severity.Success, "Settings saved");
        // Refresh domain status (saving a new domain sets it to "pending")
        api.admin.get("api/kb/settings").then(async (r) => {
          if (r.ok) {
            const d = await r.json();
            setDomainStatus(d?.domainStatus ?? null);
            setEffectiveDomain(d?.effectiveDomain ?? "");
          }
        }).catch(() => {});
      }
    } catch (err) {
      console.error("Failed to save KB settings:", err);
      toastAlert.displayToast(Severity.Error, "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  async function triggerSync() {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const resp = await api.admin.post("api/kb/settings/sync", {});
      const data = await resp.json();
      setSyncMessage(data?.message ?? "Sync triggered.");
    } catch {
      setSyncMessage("Sync request failed.");
    } finally {
      setSyncing(false);
    }
  }

  async function triggerReindex() {
    setReindexing(true);
    setReindexResult(null);
    try {
      const resp = await api.admin.post("api/kb/search/reindex", {});
      const data = await resp.json();
      if (data?.success) {
        setReindexResult({ count: data.count, message: data.message });
        toastAlert.displayToast(Severity.Success, `Reindex complete — ${data.count} articles indexed`);
        // Refresh health stats after successful reindex
        api.admin.get("api/kb/search/status").then(async (r) => {
          if (r.ok) {
            const s = await r.json();
            setLastReindexAt(s?.lastReindexAt ?? null);
            setLastReindexCount(s?.lastReindexCount ?? 0);
            setStaleCount(s?.staleCount ?? 0);
            setTotalPublished(s?.totalPublished ?? 0);
          }
        }).catch(() => {});
      } else {
        toastAlert.displayToast(Severity.Error, data?.message ?? "Reindex failed");
      }
    } catch (err) {
      console.error("Reindex error:", err);
      toastAlert.displayToast(Severity.Error, "Reindex failed");
    } finally {
      setReindexing(false);
    }
  }

  async function runPreview(e?: React.FormEvent) {
    e?.preventDefault();
    if (!previewQuery.trim()) return;
    setPreviewing(true);
    setPreviewResults([]);
    try {
      const r = await api.admin.get(`api/kb/search/preview?q=${encodeURIComponent(previewQuery)}`);
      const data = await r.json();
      setPreviewResults(Array.isArray(data?.results) ? data.results : []);
    } catch {
      toastAlert.displayToast(Severity.Error, "Search preview failed");
    } finally {
      setPreviewing(false);
    }
  }

  function formatRelativeTime(isoDate: string | null): string {
    if (!isoDate) return "never";
    try {
      const d = new Date(isoDate);
      const diffMs = Date.now() - d.getTime();
      const mins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMs / 3600000);
      const days = Math.floor(diffMs / 86400000);
      if (mins < 2) return "just now";
      if (mins < 60) return `${mins}m ago`;
      if (hours < 24) return `${hours}h ago`;
      return `${days}d ago`;
    } catch { return "unknown"; }
  }

  async function uploadFile(file: File) {
    try {
      const formData = new FormData();
      formData.append("logo", file);

      const response = await api.admin.postFormData("api/kb/settings/upload-logo", formData);
      const json = await response.json();
      setLogoUrl(json?.url ?? "");
      toastAlert.displayToast(Severity.Success, "Logo uploaded");
    } catch (err) {
      console.error("Failed to upload logo:", err);
      toastAlert.displayToast(Severity.Error, "Failed to upload logo");
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (connected === null) return null;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Sticky Save Bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm -mx-6 px-6 py-3 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-slate-800">
            Knowledge Base Settings
          </h1>
          <p className="text-xs text-slate-500">
            Configure how your public knowledge base appears to customers
          </p>
        </div>
        <div className="flex-shrink-0">
          <PrimaryBlueButton
            label={saving ? "Saving..." : "Save Settings"}
            disabled={saving}
            onClick={() => handleSubmit()}
          />
        </div>
      </div>

      {/* Sync Error Banner — shown when the last background sync failed */}
      {syncError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <i className="fa fa-circle-exclamation text-red-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-800">
            <span className="font-medium">Help article sync failed — </span>
            {syncError}
          </div>
        </div>
      )}

      {/* Approaching Limit Banner — shown only when usage is notable */}
      {usageLoaded && (() => {
        const EMBEDDING_WARN = 700;  // warn at 700+ calls/month
        const REINDEX_WARN   = 15;   // warn at 15+ reindex ops/month
        const warnings: string[] = [];
        if (embeddingCalls >= EMBEDDING_WARN) warnings.push(`AI indexing calls: ${embeddingCalls.toLocaleString()} this month`);
        if (reindexOps >= REINDEX_WARN) warnings.push(`Reindex operations: ${reindexOps} this month`);
        if (warnings.length === 0) return null;
        return (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <i className="fa fa-triangle-exclamation text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <span className="font-medium">Usage approaching limit — </span>
              {warnings.join(" · ")}
              <span className="text-amber-600 ml-1">
                · <a href="/messaging/billing" className="underline hover:text-amber-900">View plan details</a>
              </span>
            </div>
          </div>
        );
      })()}

      {/* Info Dropdown */}
      <div className="mb-6">
        <button
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
          onClick={() => setShowHelp(!showHelp)}
        >
          <i className={`fa fa-chevron-${showHelp ? "down" : "right"} text-xs`} />
          <i className="fa fa-circle-info" />
          How KB settings work
        </button>
        {showHelp && (
          <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-slate-700 space-y-2">
            <p>
              <strong>Branding</strong> settings control the appearance of your
              public-facing knowledge base site. Customers see the company name,
              logo, theme color, and header text.
            </p>
            <p>
              <strong>Return Link</strong> lets you add a "Back to [your site]"
              link at the top of the KB, so customers can navigate back to your
              main website.
            </p>
            <p>
              <strong>Live Chat</strong> integration adds a chat widget to your
              KB pages. When enabled, customers can start a live chat directly
              from any KB article if they need more help.
            </p>
            <p>
              <strong>Bot integration:</strong> Articles with "Enable as bot response"
              can be surfaced by your AI chatbot. The bot uses the article's bot
              summary and can send a link to the full article.
            </p>
          </div>
        )}
      </div>

      {/* KB Public URL Section */}
      <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
        <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
          <i className="fa fa-globe text-blue-500" />
          Your Knowledge Base URL
        </h2>

        {effectiveDomain && (
          <div className={`flex items-center gap-3 mb-4 p-3 rounded-lg border ${!domain ? "bg-blue-50 border-blue-200" : "bg-green-50 border-green-200"}`}>
            <i className={`fa ${!domain ? "fa-globe text-blue-500" : "fa-check-circle text-green-500"} flex-shrink-0`} />
            <div className="min-w-0 flex-1">
              <a
                href={`https://${effectiveDomain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
              >
                https://{effectiveDomain}
              </a>
              <p className="text-xs mt-0.5 text-slate-500">
                {!domain
                  ? "Default URL — active once Velaro provisions your subdomain (no DNS setup needed on your end)."
                  : "Custom domain — share this link with customers."}
              </p>
            </div>
            <button
              type="button"
              className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50 flex-shrink-0"
              aria-label="Copy URL"
              onClick={() => {
                navigator.clipboard.writeText(`https://${effectiveDomain}`);
                setUrlCopied(true);
                setTimeout(() => setUrlCopied(false), 2000);
              }}
            >
              <i className={`fa ${urlCopied ? "fa-check text-green-500" : "fa-copy"} text-sm`} />
            </button>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="kb-domain" className="block text-sm font-medium text-slate-700">
                Custom Domain <span className="text-xs font-normal text-slate-400">(optional)</span>
              </label>
              {domainStatus === "active" && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  Active
                </span>
              )}
              {domainStatus === "pending" && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  Pending provisioning
                </span>
              )}
            </div>
            <input
              type="text"
              id="kb-domain"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
              placeholder="help.yourcompany.com"
              value={domain}
              onChange={(e) => setDomain(e.currentTarget.value)}
            />
            <p className="text-xs text-slate-400 mt-1">
              Your default <strong>{effectiveDomain && !domain ? effectiveDomain : "yourcompany.kb.livefluence.com"}</strong> subdomain is always active. Enter a custom domain and follow the setup guide — Velaro will provision SSL and confirm when it's live.
            </p>
          </div>

          {/* CNAME target — shown once a domain is saved so customer can act immediately */}
          {domain && frontDoorEndpoint && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs space-y-1">
              <p className="font-medium text-slate-700">Add this CNAME record in your DNS provider:</p>
              <div className="bg-white rounded border border-blue-200 p-2 font-mono text-[11px] leading-5">
                <div><span className="text-slate-400">Name:</span> <span className="text-slate-800">{domain.split(".")[0]}</span></div>
                <div><span className="text-slate-400">Type:</span> <span className="text-slate-800">CNAME</span></div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Value:</span>
                  <span className="text-slate-800">{frontDoorEndpoint}</span>
                  <button
                    type="button"
                    className="text-blue-500 hover:text-blue-700"
                    onClick={() => navigator.clipboard.writeText(frontDoorEndpoint)}
                    title="Copy"
                  >
                    <i className="fa fa-copy text-[10px]" />
                  </button>
                </div>
              </div>
              {domainStatus === "pending" && (
                <p className="text-amber-700">
                  After adding the CNAME, email <a href="mailto:support@velaro.com" className="underline">support@velaro.com</a> — we'll provision SSL and activate your domain (usually 1 business day).
                </p>
              )}
            </div>
          )}
          {/* Custom domain setup guide */}
          <details className="group">
            <summary className="cursor-pointer text-xs font-medium text-blue-600 hover:text-blue-800 select-none list-none flex items-center gap-1">
              <i className="fa fa-chevron-right text-[10px] group-open:rotate-90 transition-transform" />
              How to set up a custom domain
            </summary>
            <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-4 text-xs text-slate-700 space-y-3">
              <p className="font-medium text-slate-800">3 steps — takes about 5 minutes plus DNS propagation.</p>

              <div>
                <p><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-white font-bold text-[10px] mr-1">1</span>
                  Enter your desired domain in the field above and save. Your CNAME target will appear automatically.</p>
              </div>

              <div>
                <p><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-white font-bold text-[10px] mr-1">2</span>
                  <strong>Add a CNAME in your DNS provider</strong> (GoDaddy, Cloudflare, Route 53, etc.) pointing to the value shown above.</p>
              </div>

              <div>
                <p><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-white font-bold text-[10px] mr-1">3</span>
                  <strong>Email <a href="mailto:support@velaro.com" className="underline">support@velaro.com</a></strong> — we'll provision a free SSL certificate and activate your domain (usually 1 business day). Your default <strong>{effectiveDomain || "yourcompany.kb.livefluence.com"}</strong> URL continues to work the whole time.</p>
              </div>

              <p className="text-slate-500 pt-1 border-t border-blue-100">SSL is fully managed by Velaro — no certificate purchase needed on your end.</p>
            </div>
          </details>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Branding Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-palette text-blue-500" />
            Branding
          </h2>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="company-name"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Company Name
              </label>
              <input
                type="text"
                id="company-name"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Your company name"
                value={companyName}
                onChange={(e) => setCompanyName(e.currentTarget.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Logo
              </label>
              {logoUrl && (
                <div className="flex items-center gap-3 mb-2 p-3 bg-slate-50 rounded-lg">
                  <img
                    src={logoUrl}
                    alt="KB Logo"
                    className="h-10 object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <button
                    type="button"
                    className="p-1.5 text-red-500 hover:text-red-700 rounded hover:bg-red-50"
                    onClick={() => setLogoUrl("")}
                    aria-label="Remove logo"
                  >
                    <i className="fa fa-trash text-sm" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 mt-1">
                <UploadButton onChange={(file) => uploadFile(file)} />
                <span className="text-xs text-slate-400">or paste a URL:</span>
                <input
                  type="url"
                  className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://..."
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.currentTarget.value)}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Upload an image file or paste a public image URL (PNG, SVG, JPG)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Theme Color
              </label>
              <ColorPicker
                value={themeColor}
                onChange={(value) => setThemeColor(value)}
              />
            </div>

            <div>
              <label
                htmlFor="header-text"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Header Text
              </label>
              <input
                type="text"
                id="header-text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., How can we help you?"
                value={headerText}
                onChange={(e) => setHeaderText(e.currentTarget.value)}
              />
              <p className="text-xs text-slate-400 mt-1">
                Displayed at the top of your knowledge base homepage
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-link text-blue-500" />
            Navigation
          </h2>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="return-link-text"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Return Link Text
              </label>
              <input
                type="text"
                id="return-link-text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Back to Our Website"
                value={returnLinkText}
                onChange={(e) => setReturnLinkText(e.currentTarget.value)}
              />
            </div>

            <div>
              <label
                htmlFor="return-link-url"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Return Link URL
              </label>
              <input
                type="url"
                id="return-link-url"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://yourwebsite.com"
                value={returnLinkUrl}
                onChange={(e) => setReturnLinkUrl(e.currentTarget.value)}
              />
              <p className="text-xs text-slate-400 mt-1">
                Adds a back link at the top of your KB for customers to return
                to your main site
              </p>
            </div>
          </div>
        </div>

        {/* Article Display Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-file-lines text-blue-500" />
            Article Display
          </h2>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={showLastUpdated}
                onChange={(e) => setShowLastUpdated(e.currentTarget.checked)}
                className="rounded border-slate-300"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">
                  Show "Last updated" date on articles
                </span>
                <p className="text-xs text-slate-400 mt-0.5">
                  Displays the date the article was last edited below each article title.
                  Uncheck to hide dates (useful if you prefer a timeless look or update articles frequently).
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* KB Bot Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-robot text-blue-500" />
            Chat Bot on KB Pages
          </h2>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableKbBot}
                onChange={(e) => setEnableKbBot(e.currentTarget.checked)}
                className="rounded border-slate-300"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">
                  Show Velaro bot on KB pages
                </span>
                <p className="text-xs text-slate-400 mt-0.5">
                  Embeds your Velaro V20 chat widget on every KB page. Configure a
                  KB-specific deployment in <strong>Messaging → Deployments</strong> with the
                  workflow you want triggered for KB visitors.
                </p>
              </div>
            </label>

            {enableKbBot && (
              <div>
                <label
                  htmlFor="kb-chat-window-id"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Deployment ID (Chat Window ID)
                </label>
                <input
                  id="kb-chat-window-id"
                  type="number"
                  min={0}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={kbChatWindowId || ""}
                  placeholder="0 — use site default deployment"
                  onChange={(e) => setKbChatWindowId(parseInt(e.currentTarget.value) || 0)}
                />
                <p className="text-xs text-slate-400 mt-1">
                  The ID of the deployment configured with your KB workflow. Leave 0 to use
                  your site's default deployment. Find this ID in <strong>Messaging → Deployments</strong>.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Live Chat Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-comments text-blue-500" />
            Live Chat Integration
          </h2>

          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableLiveChat}
                onChange={(e) => setEnableLiveChat(e.currentTarget.checked)}
                className="rounded border-slate-300"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">
                  Enable live chat on KB pages
                </span>
                <p className="text-xs text-slate-400">
                  Show a chat widget on your knowledge base so customers can get
                  help from a live agent
                </p>
              </div>
            </label>

            {enableLiveChat && (
              <div>
                <label
                  htmlFor="live-chat-group"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Chat Team / Group
                </label>
                <select
                  id="live-chat-group"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={liveChatGroupId}
                  onChange={(e) =>
                    setLiveChatGroupId(parseInt(e.currentTarget.value))
                  }
                >
                  <option value={0}>Select a team</option>
                  {(teams ?? []).map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Chats from KB pages will be routed to this team
                </p>
              </div>
            )}
          </div>
        </div>

      </form>

      {/* Copilot KB Article Sync */}
      <div className="bg-white rounded-lg border shadow-sm p-6 mt-6">
        <h2 className="text-lg font-medium text-slate-800 mb-1 flex items-center gap-2">
          <i className="fa fa-arrows-rotate text-blue-500" />
          Sync Help Articles
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Velaro's product documentation is automatically synced to your KB and organized
          into categories (Getting Started, Bots &amp; AI, Workflows, Integrations, etc.).
          Sync runs automatically every 24 hours — trigger manually if you need it now.
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={syncing}
            onClick={triggerSync}
          >
            {syncing ? (
              <><i className="fa fa-circle-notch fa-spin" /> Syncing…</>
            ) : (
              <><i className="fa fa-cloud-arrow-down" /> Re-sync Articles</>
            )}
          </button>
          {syncMessage && !syncing && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <i className="fa fa-check-circle" /> {syncMessage}
            </span>
          )}
        </div>
      </div>

      {/* Azure Search Index Section */}
      <div className="bg-white rounded-lg border shadow-sm p-6 mt-6">
        <h2 className="text-lg font-medium text-slate-800 mb-1 flex items-center gap-2">
          <i className="fa fa-magnifying-glass text-blue-500" />
          AI Search Index
        </h2>
        <p className="text-sm text-slate-500 mb-2">
          Syncs your KB articles to Azure AI Search so bots can retrieve relevant
          content and answer customer questions automatically.
        </p>

        {/* How it works callout */}
        <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-slate-700 space-y-2">
          <p className="font-medium text-slate-800">How the search index works</p>
          <ul className="space-y-1.5 list-none">
            <li className="flex items-start gap-2">
              <i className="fa fa-robot text-blue-500 mt-0.5 w-4 shrink-0" />
              <span>
                <strong>Bot answers:</strong> When a visitor asks your chatbot a question,
                the bot searches this index for matching articles and uses the article's
                <strong> Bot Summary</strong> field as its reply. The bot summary is a
                short, plain-language version of the article written specifically for
                bot responses — edit it per article in the Articles tab.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <i className="fa fa-file-lines text-blue-500 mt-0.5 w-4 shrink-0" />
              <span>
                <strong>What gets indexed:</strong> Article title, body content, tags,
                and bot summary. Only <em>published</em> articles marked
                "Enable as bot response" are used for bot replies; all published
                articles are searchable on the public KB site.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <i className="fa fa-bolt text-blue-500 mt-0.5 w-4 shrink-0" />
              <span>
                <strong>Automatic updates:</strong> The index updates automatically
                whenever you publish or update an article. Use <em>Sync Index Now</em>{" "}
                to do a full resync — useful after bulk edits or first setup.
              </span>
            </li>
          </ul>
        </div>

        {searchEnabled === null && (
          <p className="text-sm text-slate-400">Checking index status…</p>
        )}

        {searchEnabled === false && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="fa fa-circle-info text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-700">
                  Azure Search not configured
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  To enable search indexing, set{" "}
                  <code className="bg-slate-100 px-1 rounded text-xs">KBSearchEndpoint</code>,{" "}
                  <code className="bg-slate-100 px-1 rounded text-xs">KBSearchApiKey</code>, and{" "}
                  <code className="bg-slate-100 px-1 rounded text-xs">KBSearchIndexName</code>{" "}
                  in the Azure App Service configuration.
                </p>
              </div>
            </div>
          </div>
        )}

        {searchEnabled === true && (
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm font-medium text-green-700">
                Azure Search connected
              </span>
              {kbAutoReindexIntervalHours > 0 && (
                <span className="text-xs text-slate-400">
                  · auto-reindex every {kbAutoReindexIntervalHours}h
                </span>
              )}
            </div>

            {/* Reindex Health Panel */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                <div className="text-lg font-semibold text-slate-800">{totalPublished}</div>
                <div className="text-xs text-slate-500 mt-0.5">Published Articles</div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                <div className="text-lg font-semibold text-slate-800">{lastReindexCount}</div>
                <div className="text-xs text-slate-500 mt-0.5">In Index</div>
              </div>
              <div className={`p-3 border rounded-lg text-center ${
                lastReindexAt === null
                  ? "bg-slate-50 border-slate-200"
                  : staleCount > 0
                  ? "bg-amber-50 border-amber-200"
                  : "bg-green-50 border-green-200"
              }`}>
                {lastReindexAt === null ? (
                  <>
                    <div className="text-sm font-semibold text-slate-500">No full sync</div>
                    <div className="text-xs mt-0.5 text-slate-400">Articles indexed on save</div>
                  </>
                ) : (
                  <>
                    <div className={`text-lg font-semibold ${staleCount > 0 ? "text-amber-700" : "text-green-700"}`}>
                      {staleCount}
                    </div>
                    <div className={`text-xs mt-0.5 ${staleCount > 0 ? "text-amber-600" : "text-green-600"}`}>
                      {staleCount > 0 ? "Need re-sync" : "Up to date"}
                    </div>
                  </>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-400 -mt-1">
              {lastReindexAt === null ? (
                <span>
                  No full sync run yet — articles are indexed individually each time you publish or save.
                  Run <strong>Sync Index Now</strong> to push all articles at once.
                </span>
              ) : (
                <span>
                  Last full sync: <span className="font-medium">{formatRelativeTime(lastReindexAt)}</span>
                  {` (${new Date(lastReindexAt).toLocaleString()})`}
                  {staleCount > 0 && (
                    <span className="text-amber-600 ml-1">
                      · {staleCount} article{staleCount !== 1 ? "s" : ""} changed since last sync
                    </span>
                  )}
                </span>
              )}
            </p>

            {/* Bot indexing toggle — only visible if AI is enabled on this subscription */}
            {hasAI && (
              <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableKbBotIndexing}
                    onChange={(e) => setEnableKbBotIndexing(e.currentTarget.checked)}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">
                      Push articles to bot search index
                    </span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      When checked, articles with "Enable as bot response" are automatically
                      pushed to the Azure AI Search index whenever published or updated.
                      The AI bot uses this index to answer visitor questions.
                      Uncheck to decouple your KB from the bot (public KB search still works).
                    </p>
                  </div>
                </label>
              </div>
            )}


            <div className="flex items-center gap-3">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={reindexing}
                onClick={triggerReindex}
              >
                {reindexing ? (
                  <>
                    <i className="fa fa-circle-notch fa-spin" />
                    Indexing…
                  </>
                ) : (
                  <>
                    <i className="fa fa-arrow-rotate-right" />
                    Sync Index Now
                  </>
                )}
              </button>

              {reindexResult && !reindexing && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <i className="fa fa-check-circle" />
                  {reindexResult.message}
                </span>
              )}
            </div>

            <p className="text-xs text-slate-400">
              Reindex rebuilds the full index from all published public articles.
              {hasAI
                ? " The AI bot uses only articles with \"Enable as bot response\" turned on — you can control this per article in the Articles list."
                : " To control which articles appear in search, publish or unpublish them individually."}
              {" "}Runs in the background — may take a few seconds for large knowledge bases.
              {kbAutoReindexIntervalHours > 0 && " Auto-reindex runs automatically on the interval above."}
            </p>

            {/* Search Preview Panel */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                onClick={() => setShowPreview(!showPreview)}
              >
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <i className="fa fa-magnifying-glass text-slate-500" />
                  Test Bot Search Preview
                  <span className="text-xs font-normal text-slate-500">
                    — what the AI bot sees when answering questions
                  </span>
                </div>
                <i className={`fa fa-chevron-${showPreview ? "up" : "down"} text-slate-400 text-xs`} />
              </button>

              {showPreview && (
                <div className="p-4 space-y-3 bg-white">
                  <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-slate-600 space-y-1">
                    <p className="font-medium text-slate-700">
                      <i className="fa fa-robot text-blue-500 mr-1" />
                      This tests Azure AI Search — the semantic search used by your chatbot
                    </p>
                    <p>
                      When a visitor asks your bot a question, the bot searches this index using <strong>semantic/vector search</strong> (meaning-based, not just keyword matching). It finds the most relevant articles and uses their <em>Bot Summary</em> to answer.
                    </p>
                    <p className="text-amber-700 border-t border-blue-100 pt-1">
                      <i className="fa fa-triangle-exclamation text-amber-500 mr-1" />
                      <strong>Note:</strong> The <em>public KB search bar</em> your customers use is separate — it runs SQL full-text search (keyword-based). The two may return different results. This preview only shows bot search results.
                    </p>
                  </div>
                  <form onSubmit={runPreview} className="flex gap-2">
                    <input
                      type="text"
                      value={previewQuery}
                      onChange={(e) => setPreviewQuery(e.target.value)}
                      placeholder='e.g. "how do I reset my password"'
                      className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="submit"
                      disabled={previewing || !previewQuery.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {previewing
                        ? <><i className="fa fa-circle-notch fa-spin" />Searching…</>
                        : <><i className="fa fa-search" />Search</>}
                    </button>
                  </form>

                  {previewResults.length > 0 && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {previewResults.map((r, i) => (
                        <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-100 bg-slate-50 hover:bg-white transition-colors">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
                            {r.score}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-medium text-slate-800 truncate">{r.title || "Untitled"}</span>
                              {r.enableBotResponse && (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-purple-100 text-purple-700">Bot</span>
                              )}
                            </div>
                            {r.topicName && (
                              <div className="text-xs text-slate-400 mt-0.5">{r.topicName}</div>
                            )}
                            {r.description && (
                              <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{r.description}</div>
                            )}
                          </div>
                          {r.url && (
                            <a href={r.url} target="_blank" rel="noopener noreferrer"
                              className="flex-shrink-0 text-slate-400 hover:text-blue-600 p-1"
                              title="Open article">
                              <i className="fa fa-external-link text-xs" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!previewing && previewQuery && previewResults.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No results — try different keywords or reindex if articles are missing.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Monthly Usage Card */}
      {usageLoaded && (
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-1 flex items-center gap-2">
            <i className="fa fa-chart-bar text-blue-500" />
            Monthly Usage
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            {usageMonth ? `${usageMonth} · ` : ""}Resets on the 1st of each month.{" "}
            <a href="/messaging/billing" className="text-blue-500 hover:underline">View plan limits →</a>
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div className={`text-2xl font-semibold ${embeddingCalls >= 700 ? "text-amber-600" : "text-slate-800"}`}>
                {embeddingCalls.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">AI Indexing Calls</div>
              <div className="text-xs text-slate-400 mt-1">Embedding API calls for bot search</div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div className={`text-2xl font-semibold ${reindexOps >= 15 ? "text-amber-600" : "text-slate-800"}`}>
                {reindexOps.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">Reindex Operations</div>
              <div className="text-xs text-slate-400 mt-1">Full article reindex runs this month</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
